import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import {
  Button,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { getEditableSegmentsCustomerApi, editMarketingSegmentsCustomerApi } from 'api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { BackdropCircular } from 'components';
import { Formik } from 'formik';
import * as yup from 'yup';


const SegmentsCustomerEdit = ({customerId, setOpenSegmentsCustomerEdit}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    getEditableSegmentsCustomerApi({dispatch, id: customerId});
  }, []);

  const {
    options,
    isEavAttributeLoading
  } = useSelector((state: IReduxState) => state.eavAttributes);

  const {
    editableMarketingSegmentsCustomer,
    isLoadingEditableMarketingSegmentsCustomer
  } = useSelector((state: IReduxState) => state.quote_marketing_segments);

  const handleSaveCustomerData = (values) => {
    const customerData = {
      ...editableMarketingSegmentsCustomer,
      firstname: values.firstname,
      lastname: values.lastname,
      id: customerId,
      email: values.email,
      custom_attributes: [
        ...editableMarketingSegmentsCustomer.custom_attributes,
        { attribute_code: 'phone_number', value: values.phone },
        { attribute_code: 'marketing_segments', value: values.marketing_segments.map((item) => item.value).join(',') }
      ],
    };

    const isUpdated = editMarketingSegmentsCustomerApi({dispatch, customerId, customerData});

    if (isUpdated) {
      setOpenSegmentsCustomerEdit(false);
    }
  };

  const customerPhone = editableMarketingSegmentsCustomer?.custom_attributes?.find(
    (attr) => attr.attribute_code === 'phone_number')?.value;

  const customerMarketingSegments = (editableMarketingSegmentsCustomer?.custom_attributes?.find(
    (attr) => attr.attribute_code === 'marketing_segments'
  )?.value ?? '').split(',').map((value) => value.trim());

  const marketingSegment = options.filter((option) => {
    const optionValue = option?.value;
    return Array.isArray(optionValue)
      ? optionValue.some((value) => customerMarketingSegments?.includes(value))
      : customerMarketingSegments?.includes(optionValue);
  });

  return (
    <>
      <Formik
        initialValues={{
          firstname: editableMarketingSegmentsCustomer?.firstname || '',
          lastname: editableMarketingSegmentsCustomer?.lastname || '',
          email: editableMarketingSegmentsCustomer?.email || '',
          phone: customerPhone || null,
          marketing_segments: marketingSegment || []
        }}
        validationSchema={yup.object({
          firstname: yup
            .string()
            .required('Field is required'),
          lastname: yup
            .string()
            .required('Field is required'),
          email: yup
            .string()
            .email()
            .required('Field is required'),
          phone: yup
            .string()
            .min(11, 'Enter valid phone number')
            .max(11, 'Enter valid phone number')
            .required('Field is required')
        })}
        enableReinitialize
        validateOnMount
        onSubmit={(values) => handleSaveCustomerData(values)}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => (
          <form noValidate autoComplete="off" className="py-4 px-2" onSubmit={handleSubmit}>
            <div className="flex-grow scroll-y pb-5">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold"
                  >
                    First Name
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="First Name"
                    name="firstname"
                    className="max-w-600 mt-2 ml-2"
                    value={values.firstname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.firstname && Boolean(errors.firstname)}
                    helperText={touched.firstname && errors.firstname}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold"
                  >
                    Last Name
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Last Name"
                    name="lastname"
                    className="max-w-600 mt-2 ml-2"
                    value={values.lastname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lastname && Boolean(errors.lastname)}
                    helperText={touched.lastname && errors.lastname}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold"
                  >
                    Email
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Email"
                    name="email"
                    className="max-w-600 mt-2 ml-2"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold"
                  >
                    Phone
                  </Typography>
                  <NumberFormat
                    customInput={TextField}
                    format="+# (###) ###-####"
                    fullWidth
                    label="Phone"
                    required
                    className="max-w-600 mt-2 ml-2"
                    variant="outlined"
                    name="phone"
                    value={values.phone || null}
                    onValueChange={(value) => {
                      setFieldValue('phone', String(value.floatValue));
                    }}
                    onBlur={handleBlur}
                    error={touched?.phone && Boolean(errors?.phone)}
                    helperText={touched?.phone && errors?.phone}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold"
                  >
                    Marketing Segments
                  </Typography>
                  <Autocomplete
                    multiple
                    fullWidth
                    disableClearable
                    size="small"
                    className="max-w-600 mt-2 ml-2"
                    value={values.marketing_segments.flatMap((arr) => arr)}
                    options={options}
                    getOptionLabel={(option) => option?.label}
                    getOptionSelected={(option, value) => option?.value === value?.value}
                    filterSelectedOptions
                    onChange={(e, value) => setFieldValue('marketing_segments', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Marketing Segment"
                        variant="outlined"
                        error={touched.marketing_segments && Boolean(errors.marketing_segments)}
                        helperText={touched.marketing_segments && errors.marketing_segments}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <div className="mt-10">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="uppercase mr-2"
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  className="uppercase"
                  onClick={() => setOpenSegmentsCustomerEdit(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <BackdropCircular loading={isLoadingEditableMarketingSegmentsCustomer || isEavAttributeLoading} />
    </>
  );
};

export default SegmentsCustomerEdit;
